import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import * as Icons from "lucide-react";
import { PUBLIC_BASE } from "../lib/api";

/**
 * Inline floor-plan viewer.  Tries to render whatever doc is uploaded
 * — PDF gets an iframe, image gets a tap-to-zoom <img>, anything else
 * gracefully falls back to a download link.  No friction: viewer sees
 * the plan instantly without leaving the page.
 */
export default function FloorPlanViewer({ doc, brand, onClose }) {
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!doc) return null;

  const url = `${PUBLIC_BASE}/api/public/documents/${doc.id}`;
  const isPdf = (doc.content_type || "").startsWith("application/pdf");
  const isImage = (doc.content_type || "").startsWith("image/");

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
      onClick={onClose}
      data-testid="floorplan-viewer"
    >
      <div
        className="relative w-full max-w-5xl h-[88vh] bg-[#0A0A0A] border rounded-2xl overflow-hidden flex flex-col"
        style={{ borderColor: brand ? `${brand}50` : "rgb(255 255 255 / 0.1)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 px-5 py-3 border-b"
          style={{ borderColor: brand ? `${brand}30` : "rgb(255 255 255 / 0.1)" }}
        >
          <Icons.Ruler size={16} style={{ color: brand || "#a1a1aa" }} />
          <div className="flex-1 min-w-0">
            <div
              className="text-[10px] uppercase tracking-[0.4em]"
              style={{ color: brand || "#71717a" }}
            >
              Floor plan
            </div>
            <div className="font-display text-base text-white truncate">
              {doc.label || doc.filename || "Floor plan"}
            </div>
          </div>
          <a
            href={url}
            download={doc.filename || "floorplan"}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 rounded-md px-3 py-1.5 inline-flex items-center gap-1.5"
            data-testid="floorplan-viewer-download"
          >
            <Icons.Download size={12} />
            <span className="hidden sm:inline">Download</span>
          </a>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white p-1.5"
            data-testid="floorplan-viewer-close"
            aria-label="Close"
          >
            <Icons.X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 bg-black overflow-auto">
          {isPdf ? (
            <iframe
              src={url}
              title={doc.label || "Floor plan"}
              className="w-full h-full border-0"
              data-testid="floorplan-viewer-pdf"
            />
          ) : isImage ? (
            <button
              type="button"
              onClick={() => setZoom((z) => !z)}
              className="w-full h-full flex items-center justify-center cursor-zoom-in"
              data-testid="floorplan-viewer-image"
            >
              <img
                src={url}
                alt={doc.label || "Floor plan"}
                className={`max-w-full max-h-full transition-transform ${
                  zoom ? "scale-150 cursor-zoom-out" : ""
                }`}
              />
            </button>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-500 text-sm">
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 hover:bg-white/5"
              >
                <Icons.FileText size={14} />
                Open document
              </a>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
